/**
 * Canonical entitlement-key catalog — mirrors the C# constants at
 * flex2-api/Contracts/Services/EntitlementKeys.cs and the richer JSON catalog at
 * flex2-api/Services/Entitlements/Seeding/entitlement-catalog.json.
 *
 * Keys are grouped by **lane** per the f2core.com pricing-page taxonomy:
 * Platform Foundation, Facility Operations, Environmental, Energy Management,
 * Hardware & Devices, SpendGuard, Enterprise Action Engine. Plan K Phase 1
 * codifies this as the single source of truth on the frontend.
 *
 * Plan K Phase 2 will wire `useEntitlements` to derive lane-bundle membership
 * from the presence of any child key; sidenav grouping will then key off
 * `LANE_KEYS.*` rather than ad-hoc string checks.
 */

export const ENTITLEMENT_KEYS = Object.freeze({
  // ─── Platform Foundation (included with every plan) ─────────────────────
  SiteTelemetry: "site_telemetry",
  BasicDashboards: "basic_dashboards",

  // ─── Facility Operations lane ─────────────────────────────────────────
  Tickets: "tickets",
  WorkOrders: "work_orders",
  VendorCoordination: "vendor_coordination",
  LiftInspections: "lift_inspections",

  // ─── Environmental lane (SPCC + AFMS + labels + training) ─────────────
  Spcc: "spcc",
  Labels: "labels",
  TrainingRecords: "training_records",
  AfmsTankTelemetry: "afms_tank_telemetry",
  AfmsPickupWorkflow: "afms_pickup_workflow",

  // ─── Energy Management lane (Plan K Phase 3) ──────────────────────────
  EnergyRuntimeAnalytics: "energy_runtime_analytics",
  EnergyHvacMonitoring: "energy_hvac_monitoring",
  EnergyCompressedAir: "energy_compressed_air",
  EnergyDryerMonitoring: "energy_dryer_monitoring",
  EnergyCurrentSensing: "energy_current_sensing",

  // ─── Hardware & Devices lane (quoted separately per pricing) ──────────
  IiotPlus: "iiot_plus",
  IiotPro: "iiot_pro",
  HighFrequencyTelemetry: "high_frequency_telemetry",
  LiftLogicAdvanced: "lift_logic_advanced",
  LiftLogicIot: "lift_logic_iot",

  // ─── SpendGuard lane (Plan K Phase 4) ─────────────────────────────────
  // Essentials tier
  SpendGuardDuplicateDetection: "spendguard_duplicate_detection",
  SpendGuardOverQuoteAlerts: "spendguard_over_quote_alerts",
  SpendGuardMissingCreditAlerts: "spendguard_missing_credit_alerts",
  SpendGuardVendorScorecardsBasic: "spendguard_vendor_scorecards_basic",
  // Pro tier
  SpendGuardAiAssistant: "spendguard_ai_assistant",
  SpendGuardAnomalyDetection: "spendguard_anomaly_detection",
  SpendGuardBenchmarks: "spendguard_benchmarks",
  SpendGuardRenewalCalendar: "spendguard_renewal_calendar",
  // Enterprise sub-tiers
  SpendGuardCrossSite: "spendguard_cross_site",
  SpendGuardRegionalGovernance: "spendguard_regional_governance",
  SpendGuardPortfolioDashboards: "spendguard_portfolio_dashboards",
  SpendGuardExecutiveReporting: "spendguard_executive_reporting",

  // ─── Enterprise Action Engine lane ────────────────────────────────────
  EnterpriseBriefings: "enterprise_briefings",
  AgentRuns: "agent_runs",
  PrioritySupport: "priority_support",
});

/**
 * Lane bundle keys. A lane bundle is implied by ANY child key in that lane.
 * Platform Foundation has no bundle key — it's the implicit floor.
 * Phase 2 wires the derivation; for now this just enumerates the names.
 */
export const LANE_KEYS = Object.freeze({
  FacilityOperations: "lane.facility_operations",
  Environmental: "lane.environmental",
  EnergyManagement: "lane.energy_management",
  HardwareDevices: "lane.hardware_devices",
  SpendGuard: "lane.spendguard",
  EnterpriseActionEngine: "lane.enterprise_action_engine",
});

/**
 * Lane id → list of child entitlement keys. Mirrors the `lane` field in
 * entitlement-catalog.json. Used by Phase 2's `useEntitlements` to derive lane
 * bundle membership.
 */
export const LANE_CHILDREN = Object.freeze({
  platform_foundation: [
    ENTITLEMENT_KEYS.SiteTelemetry,
    ENTITLEMENT_KEYS.BasicDashboards,
  ],
  facility_operations: [
    ENTITLEMENT_KEYS.Tickets,
    ENTITLEMENT_KEYS.WorkOrders,
    ENTITLEMENT_KEYS.VendorCoordination,
    ENTITLEMENT_KEYS.LiftInspections,
  ],
  environmental: [
    ENTITLEMENT_KEYS.Spcc,
    ENTITLEMENT_KEYS.Labels,
    ENTITLEMENT_KEYS.TrainingRecords,
    ENTITLEMENT_KEYS.AfmsTankTelemetry,
    ENTITLEMENT_KEYS.AfmsPickupWorkflow,
  ],
  energy_management: [
    ENTITLEMENT_KEYS.EnergyRuntimeAnalytics,
    ENTITLEMENT_KEYS.EnergyHvacMonitoring,
    ENTITLEMENT_KEYS.EnergyCompressedAir,
    ENTITLEMENT_KEYS.EnergyDryerMonitoring,
    ENTITLEMENT_KEYS.EnergyCurrentSensing,
  ],
  hardware_devices: [
    ENTITLEMENT_KEYS.IiotPlus,
    ENTITLEMENT_KEYS.IiotPro,
    ENTITLEMENT_KEYS.HighFrequencyTelemetry,
    ENTITLEMENT_KEYS.LiftLogicAdvanced,
    ENTITLEMENT_KEYS.LiftLogicIot,
  ],
  spendguard: [
    ENTITLEMENT_KEYS.SpendGuardDuplicateDetection,
    ENTITLEMENT_KEYS.SpendGuardOverQuoteAlerts,
    ENTITLEMENT_KEYS.SpendGuardMissingCreditAlerts,
    ENTITLEMENT_KEYS.SpendGuardVendorScorecardsBasic,
    ENTITLEMENT_KEYS.SpendGuardAiAssistant,
    ENTITLEMENT_KEYS.SpendGuardAnomalyDetection,
    ENTITLEMENT_KEYS.SpendGuardBenchmarks,
    ENTITLEMENT_KEYS.SpendGuardRenewalCalendar,
    ENTITLEMENT_KEYS.SpendGuardCrossSite,
    ENTITLEMENT_KEYS.SpendGuardRegionalGovernance,
    ENTITLEMENT_KEYS.SpendGuardPortfolioDashboards,
    ENTITLEMENT_KEYS.SpendGuardExecutiveReporting,
  ],
  enterprise_action_engine: [
    ENTITLEMENT_KEYS.EnterpriseBriefings,
    ENTITLEMENT_KEYS.AgentRuns,
    ENTITLEMENT_KEYS.PrioritySupport,
  ],
});

/** Flat list of every feature-level entitlement key (excludes lane bundles). */
export const ALL_ENTITLEMENT_KEYS = Object.freeze(Object.values(ENTITLEMENT_KEYS));

/** Flat list of every lane bundle key. */
export const ALL_LANE_KEYS = Object.freeze(Object.values(LANE_KEYS));
